<script lang="ts">
    import type { Event } from '../../api/events';
    import { getEventById } from '../../api/events';
    import * as t from '../../translations/Events.ts';
    import EventConfirmation from '../../lib/Events/EventConfirmation.svelte';
    import ConfirmationPageSkeleton from '../../lib/Events/ConfirmationPageSkeleton.svelte';
    import {
        eventConfirmationData,
        clearEventConfirmationData
    } from '../../stores/eventsConfirmationStore';
    
    export let params: { id?: string } = {};
    
    let loading = true;
    let error: string | null = null;
    let event: Event | null = null;
    let lastLoadedEventId: number | null = null;
    let activeLoadRequest = 0;

    let thankYouMessage = '';

    async function loadEvent(eventId: number) {
        const requestId = ++activeLoadRequest;
        
        try {
            loading = true;
            error = null;
            event = null;
            thankYouMessage = '';
            
            event = await getEventById(eventId);

            // Pick the best thank-you copy in this order:
            //   1. The per-event admin message from `event.thankYouMessage`
            //      (legacy `ConfirmationDisplaySettings.ThankYouMessage`). The
            //      REST backend always populates this -- either with the admin's
            //      rich-HTML override or, when not customised, the localised
            //      default from VoterVoice.Events.DisplaySettings.
            //      ConfirmationDisplaySettings.ThankYouMessage.
            //   2. Whatever the registration POST response stashed in the store
            //      (`result.message`) -- useful for one-off messages from the
            //      server like "Your registration was upgraded to VIP". Only
            //      relevant against backends that don't fill in #1 (legacy
            //      UserSite /Api/Events, mock fixtures, etc.).
            //   3. Final client-side fallback to `t.Thank_You_Default()` so the
            //      page never collapses to just the title on a backend that
            //      forgets to supply anything.
            const storeData = $eventConfirmationData;
            const storeMessage = storeData?.eventId === eventId ? (storeData.thankYouMessage || '') : '';
            thankYouMessage = event?.thankYouMessage || storeMessage || t.Thank_You_Default();
            if (storeData?.eventId === eventId) {
                clearEventConfirmationData();
            }

            if (requestId !== activeLoadRequest) return;
        } catch (e) {
            error = t.Loading_Error();
            console.error('Failed to load event:', e);
        } finally {
            loading = false;
        }
    }

    $: {
        const eventId = params.id ? parseInt(params.id, 10) : null;

        if (eventId && !Number.isNaN(eventId)) {
            if (eventId !== lastLoadedEventId) {
                lastLoadedEventId = eventId;
                void loadEvent(eventId);
            }
        } else if (params.id) {
            loading = false;
            event = null;
            error = 'Invalid event ID';
        } else {
            loading = false;
            event = null;
            error = 'Event ID is required';
        }
    }
</script>

{#if loading}
    <ConfirmationPageSkeleton />
{:else if error}
    <div class="max-w-2xl mx-auto px-4 py-8">
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
        </div>
    </div>
{:else if event}
    <EventConfirmation
        {event}
        eventRouteId={lastLoadedEventId ?? (params.id ? parseInt(params.id, 10) : undefined)}
        {thankYouMessage}
    />
{/if}


